// 3rd party imports
import * as React from 'react';
import { inject } from 'mobx-react';

// bernie
import { AnalyticsStore, ContextStore, RouteStore } from 'bernie-core';

// shared-ui
import { LocalizedText } from '@shared-ui/localization-context';

// mobx
import { UserStore, UserAuthStore } from 'stores';

// ui
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkCard } from 'uitk-react-cards';
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkErrorSummary } from 'uitk-react-error-summary';

// util
import { getUserAnalyticsMicroMessage } from '../../util/analytics';
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';

// observability
import { ClientLogger } from 'bernie-client';
import AffiliateHubLogger from '../../util/affiliate-hub-logger';
import telemeter from '../../observability/telemeter';
import { SignupEvents, SIGNUP_TAG } from '../../observability/events/signup';

interface SignupAHConfirmationProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  user?: UserStore;
  userAuth?: UserAuthStore;
  email: string;
  verificationEmailError?: string;
}

export const SignupAHConfirmation: React.FunctionComponent<SignupAHConfirmationProps> = inject(
  'analytics',
  'userAuth',
  'routeStore',
  'user',
  'context',
)(({ analytics, context, userAuth, user, email, verificationEmailError }) => {
  const [resendVerificationEmailError, setResendVerificationEmailError] = React.useState(null);

  const logger = new AffiliateHubLogger(ClientLogger.getLoggerWithIdentifier(`${SIGNUP_TAG}.components`));

  React.useEffect(() => {
    setResendVerificationEmailError(verificationEmailError);
  }, []);

  const resendVerificationEmail = async () => {
    if (typeof analytics?.trackEvent === 'function') {
      analytics.trackEvent(
        'affhub_resend_verification_email.selected',
        'Affhub resend verification email selected',
        null,
        null,
        true,
        [getUserAnalyticsMicroMessage(userAuth?.currentUserDetails)],
      );
    }

    await user
      .sendVerificationEmail(email, modifyLocaleParam(context.locale))
      .then(() => {
        telemeter.increment(SignupEvents.VERIFICATION_EMAIL_RESENT);
        logger.info(SignupEvents.VERIFICATION_EMAIL_RESENT);
      })
      .catch((err) => {
        telemeter.increment(SignupEvents.VERIFICATION_EMAIL_RESEND_FAIL);
        logger.error(SignupEvents.VERIFICATION_EMAIL_RESEND_FAIL, JSON.stringify(err), {
          username: email,
        });
        setResendVerificationEmailError('Email communication failed. Please try again');
      });
  };

  return (
    <>
      <UitkSpacing margin={{ blockstart: 'twelve' }}>
        <UitkHeading align="center" tag="h1" size={2}>
          <LocalizedText message="signup.confirmation.title.activation_email_sent" />
        </UitkHeading>
      </UitkSpacing>
      <UitkSpacing padding={{ block: 'twelve' }}>
        <UitkLayoutFlex direction="column">
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkText align="center" size={400}>
                <LocalizedText message="signup.confirmation.message.we_have_sent_an_email" />
              </UitkText>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkCard className="break-word" padded={true}>
                <UitkText align="center" size={400} weight="bold">
                  {email}
                </UitkText>
              </UitkCard>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockend: 'six' }}>
              <UitkText align="center" size={400}>
                <LocalizedText message="signup.confirmation.message.follow_the_link_within_email" />
              </UitkText>
            </UitkSpacing>
          </UitkLayoutFlexItem>

          {resendVerificationEmailError && (
            <UitkLayoutFlexItem alignSelf="center">
              <UitkSpacing margin={{ block: 'six' }}>
                <UitkErrorSummary heading={resendVerificationEmailError} />
              </UitkSpacing>
            </UitkLayoutFlexItem>
          )}

          <UitkLayoutFlexItem alignSelf="center">
            <UitkSpacing margin={{ blockstart: 'three' }}>
              <UitkSecondaryButton onClick={resendVerificationEmail}>
                <LocalizedText message="signup.confirmation.resend_email" />
              </UitkSecondaryButton>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkSpacing>
    </>
  );
});
